import { type PayloadAction, createSlice, current } from '@reduxjs/toolkit';

import {
    type CartItem,
    getCartItemCustomizations,
    getDefaultDressings,
    initCustomization,
    isByo,
    isByoMarketPlate,
    isDressing,
    isMarketPlate,
    isNoDressing,
    menuItemToCartItem,
    normalizeCustomizations,
    setCustomizations,
    validateByo,
    validateByoMarketPlate,
    validateCustomization
} from '@lib/entities/Cart';
import {
    type Customization,
    type IngredientCategory,
    type Menu,
    type MenuItem,
    defaultIngredientSort,
    getApplicableIngredientCategories,
    getApplicableIngredients,
    isWarmBowl
} from '@lib/entities/Menu';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { keyedIndex, replace } from '@lib/utils/arrays';

/**
 * Redux twin of lib/state/Customization.ts (JSAD-7433).
 *
 * The Zustand slice reads the menu from ANOTHER slice (`api.getState().menu`,
 * Customization.ts:106). A reducer cannot, so every action that needs it receives the menu
 * through `meta.menu` (`prepare(payload, menu)`); the thin hook supplies it from the store.
 */
export interface CustomizeResult {
    accepted: boolean;
    /** The validation message the Zustand action toasted (`showError(error)`). */
    error?: string;
    /** The message the Zustand action `console.warn`ed. */
    warn?: string;
}

export interface CustomizationState {
    categories: IngredientCategory[];
    /**
     * A list of all possible customizations that ALSO reflects the
     * current state of the `draftItem`, including those with a
     * `quantity` of `0`.
     */
    customizations: Customization[];
    /**
     * All customizations that currently apply to the `draftItem`,
     * i.e. only those customizations with a `quantity >= 1`
     */
    draftCustomizations: Customization[];
    /**
     * A cart item currently being edited
     */
    draftItem?: CartItem;
    /**
     * The previous version of the cart item before this editing
     * session
     */
    lastSaved?: CartItem;
    isByo?: boolean;
    isWarm?: boolean;
    isMarketPlate?: boolean;
    isByoMarketPlate?: boolean;
    byoErrorMessage?: string;
    /**
     * Outcome of the most recent `onCustomize`. The Zustand action toasts the validation
     * error and returns a boolean synchronously (Customization.ts:181-194); a reducer can do
     * neither, so it records the outcome here. `useCustomization.redux` dispatches, reads the
     * store in the same tick, toasts `error` / warns `warn`, and returns `accepted`.
     */
    lastCustomizeResult?: CustomizeResult;
}

export interface MenuMeta {
    menu: Menu;
}

type MenuAction<P> = PayloadAction<P, string, MenuMeta>;

const withMenu =
    <P>() =>
    (payload: P, menu: Menu) => ({ payload, meta: { menu } });

const menuOnly = (menu: Menu) => ({ payload: undefined, meta: { menu } });

export interface OnCustomizePayload {
    customization: Customization;
    delta: number;
}

type DataFields = Omit<CustomizationState, 'lastCustomizeResult'>;

const DefaultCustomizationState: DataFields = {
    categories: [],
    customizations: [],
    draftCustomizations: [],
    draftItem: undefined,
    lastSaved: undefined,
    isByo: undefined,
    isWarm: undefined,
    isMarketPlate: undefined,
    isByoMarketPlate: undefined,
    byoErrorMessage: undefined
};

const initialState: CustomizationState = {
    ...DefaultCustomizationState
};

const isWarm = (item: CartItem) => {
    const menuItem = item.menuItem;

    if (!menuItem) {
        return false;
    }

    if (isWarmBowl(menuItem)) {
        return true;
    }

    return false;
};

/** Pure port of `setDraftItem` (Customization.ts:99-146). */
const withDraftItem = (
    state: CustomizationState,
    draftItem: CartItem | undefined,
    menu: Menu
): CustomizationState => {
    if (!draftItem) {
        return { ...state, ...DefaultCustomizationState };
    }

    // update our categories if we're changing the kind of menu item
    const categories =
        state.draftItem?.menuItem.id !== draftItem.menuItem.id
            ? getApplicableIngredientCategories(menu, draftItem.menuItem)
            : state.categories;

    const draftCustomizations = getCartItemCustomizations(draftItem);
    const customizationLookup = keyedIndex(draftCustomizations, (c) => c.ingredient.id);
    const ingredients = getApplicableIngredients(menu, draftItem.menuItem);

    const customizations = ingredients
        .map((ingredient) => {
            const existing = customizationLookup.get(ingredient.id);

            if (existing) {
                // If we have an existing customization, we should still use the
                // updated ingredient from getApplicableIngredients
                return {
                    ...existing,
                    ingredient: ingredient // Use the modified ingredient
                };
            }

            return initCustomization(ingredient);
        })
        .sort(defaultIngredientSort);

    return {
        ...state,
        categories,
        customizations,
        draftCustomizations,
        draftItem,
        isByo: isByo(draftItem),
        isWarm: isWarm(draftItem),
        isMarketPlate: isMarketPlate(draftItem),
        isByoMarketPlate: isByoMarketPlate(draftItem),
        byoErrorMessage:
            validateByo(draftItem, draftCustomizations) ||
            validateByoMarketPlate(draftItem, draftCustomizations)
    };
};

/** Pure port of `onCustomize` (Customization.ts:159-274); the boolean becomes `lastCustomizeResult`. */
const customize = (
    state: CustomizationState,
    customization: Customization,
    delta: number,
    menu: Menu
): CustomizationState => {
    const { categories, draftItem, draftCustomizations, isByo: draftIsByo } = state;

    if (!draftItem) {
        return { ...state, lastCustomizeResult: { accepted: false } };
    }

    const rejected = (result: Omit<CustomizeResult, 'accepted'> = {}): CustomizationState => ({
        ...state,
        lastCustomizeResult: { accepted: false, ...result }
    });
    const accepted = (nextDraftItem: CartItem): CustomizationState => ({
        ...withDraftItem(state, nextDraftItem, menu),
        lastCustomizeResult: { accepted: true }
    });

    const quantity = customization.quantity + delta;
    const existing = draftCustomizations.find(
        (c) => c.ingredient.id === customization.ingredient.id
    );
    const nextCustomization = { ...customization, quantity };

    const error = validateCustomization(
        draftCustomizations,
        nextCustomization,
        categories,
        draftItem.menuItem.id,
        // Base recipe: curated items' included ingredients aren't rows —
        // without them, category minimums miscount (JSAD greens bug).
        draftItem.menuItem.baselineCustomizations || []
    );
    if (error) {
        return rejected({ error });
    }

    // we're removing a customization
    if (quantity <= 0) {
        if (!existing) {
            // we're done, this is a no-op, but it's weird. we should have an existing ingredient
            return rejected({
                warn: "Tried to remove a customization that doesn't exist on the draft item"
            });
        }

        // in this case, we re-add the default dressing ingredients to the draft item
        if (isNoDressing(existing)) {
            const dressings = getDefaultDressings(draftItem);
            const nextCustomizations = dressings.concat(
                draftCustomizations.filter((c) => !isNoDressing(c))
            );

            return accepted(setCustomizations(draftItem, nextCustomizations));
        }

        // otherwise zero out the customization and we're done
        const nextCustomizations = replace(
            draftCustomizations,
            existing,
            initCustomization(existing.ingredient, 0)
        );

        // If the menu item is BYO, we remove the 0 quantity items to avoid showing "removed ABC" ingredient in the cart
        if (draftIsByo) {
            const customzationWithQuantity = nextCustomizations.filter((c) => c.quantity > 0);
            return accepted(setCustomizations(draftItem, customzationWithQuantity));
        }

        return accepted(setCustomizations(draftItem, nextCustomizations));
    }

    // we're adding an ingredient or incrementing the quantity

    // first check our favorite pseudo-ingredient
    if (isNoDressing(nextCustomization)) {
        if (existing?.ingredient === nextCustomization.ingredient) {
            // nothing to do here, you can't have _two_ "no dressings"
            return rejected();
        }

        // zero out any existing dressings (we want to be ableto say we removed the dressing)
        const nextCustomizations = [nextCustomization].concat(
            draftCustomizations.map((c) => (isDressing(c) ? initCustomization(c.ingredient, 0) : c))
        );

        const nextDraftItem = setCustomizations(draftItem, nextCustomizations);

        return accepted({
            ...nextDraftItem,
            dressingMixedIn: null
        });
    }

    // if we're adding a dressing we need to filter out the "no
    // dressing" pseudo customization
    const filtered = isDressing(customization)
        ? draftCustomizations.filter((c) => !isNoDressing(c))
        : draftCustomizations;

    // maintain order if we have an existing customization, otherwise prepend
    const nextCustomizations = existing
        ? replace(
              filtered,
              customization,
              nextCustomization,
              (a, b) => a.ingredient.id === b.ingredient.id
          )
        : [nextCustomization].concat(filtered);

    return accepted(setCustomizations(draftItem, nextCustomizations));
};

const customizationSlice = createSlice({
    name: 'customization',
    initialState,
    reducers: {
        initDraftItem: {
            reducer: (state, action: MenuAction<MenuItem>) =>
                withDraftItem(current(state), menuItemToCartItem(action.payload), action.meta.menu),
            prepare: withMenu<MenuItem>()
        },
        setDraftItem: {
            reducer: (state, action: MenuAction<CartItem | undefined>) =>
                withDraftItem(current(state), action.payload, action.meta.menu),
            prepare: withMenu<CartItem | undefined>()
        },
        updateDraftItem: {
            reducer: (state, action: MenuAction<Partial<CartItem>>) => {
                const plain = current(state);

                if (!plain.draftItem) {
                    return;
                }

                return withDraftItem(
                    plain,
                    { ...plain.draftItem, ...action.payload },
                    action.meta.menu
                );
            },
            prepare: withMenu<Partial<CartItem>>()
        },
        onCustomize: {
            reducer: (state, action: MenuAction<OnCustomizePayload>) =>
                customize(
                    current(state),
                    action.payload.customization,
                    action.payload.delta,
                    action.meta.menu
                ),
            prepare: withMenu<OnCustomizePayload>()
        },
        /** `onConfigure(updates)` is `updateDraftItem(updates)` (Customization.ts:275). */
        onConfigure: {
            reducer: (state, action: MenuAction<Partial<CartItem>>) => {
                const plain = current(state);

                if (!plain.draftItem) {
                    return;
                }

                return withDraftItem(
                    plain,
                    { ...plain.draftItem, ...action.payload },
                    action.meta.menu
                );
            },
            prepare: withMenu<Partial<CartItem>>()
        },
        edit: (state) => {
            state.lastSaved = state.draftItem;
        },
        cancel: {
            reducer: (state, action: MenuAction<undefined>) => {
                const plain = current(state);
                return withDraftItem(plain, plain.lastSaved, action.meta.menu);
            },
            prepare: menuOnly
        },
        /**
         * Normalizes the draft and clears `lastSaved`. The Zustand action returned the
         * normalized item; the hook reads `draftItem` back from the store after dispatching.
         */
        save: {
            reducer: (state, action: MenuAction<undefined>) => {
                const plain = current(state);

                if (!plain.draftItem) {
                    return;
                }

                const nextDraftItem = normalizeCustomizations(plain.draftItem);

                return withDraftItem(
                    { ...plain, lastSaved: undefined },
                    nextDraftItem,
                    action.meta.menu
                );
            },
            prepare: menuOnly
        },
        resetCustomization: (state) => {
            Object.assign(state, DefaultCustomizationState);
        }
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `resetCustomizations()` (useAuth.ts:121).
        builder.addCase(loggedOut, (state) => {
            Object.assign(state, DefaultCustomizationState);
        });
    }
});

export const {
    initDraftItem,
    setDraftItem,
    updateDraftItem,
    onCustomize,
    onConfigure,
    edit,
    cancel,
    save,
    resetCustomization
} = customizationSlice.actions;

export const selectCustomization = (state: { customization: CustomizationState }) =>
    state.customization;
export const selectDraftItem = (state: { customization: CustomizationState }) =>
    state.customization.draftItem;
export const selectLastCustomizeResult = (state: { customization: CustomizationState }) =>
    state.customization.lastCustomizeResult;

export default customizationSlice.reducer;
